import React, { useCallback, useEffect } from 'react';
import { Flex, Modal, ModalFuncProps, Spin, theme, Typography } from 'antd';
import { useAppStore } from '../../common/store';
import { WorkflowType } from '../../constants';
import { DocgenClient } from '../../data/docgenClient';
import { handleError } from '../../data/errorHandler';
import { CopyFileFromSharefilePayload } from '../../data/interfaces/DocumentTemplateFile';
import { t } from '../../utils';
import Prepare from '../Prepare';
import Review from '../Review';
import Start from '../Start';

const { Text } = Typography;

export const CreateDocumentTemplate = props => {
	const onDrawerClose = props.completeInteraction;
	const {
		documentTemplate,
		createDocumentTemplate,
		getDocumentTemplate,
		updateTemplateMetadata,
		templateMetadata,
		fetchThumbnailUrl,
		resetStore,
	} = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
		createDocumentTemplate: state.createDocumentTemplate,
		getDocumentTemplate: state.getDocumentTemplate,
		updateTemplateMetadata: state.updateTemplateMetadata,
		templateMetadata: state.templateMetadata,
		resetStore: state.resetStore,
		fetchThumbnailUrl: state.fetchThumbnailUrl,
	}));

	useEffect(() => {
		const onMount = async () => {
			try {
				if (!documentTemplate) {
					const templateRID = await createDocumentTemplate();
					if (
						templateMetadata?.workflow === WorkflowType.ContentViewer ||
						templateMetadata?.workflow === WorkflowType.ContextMenu
					) {
						try {
							const payload: CopyFileFromSharefilePayload = {
								fileName: templateMetadata?.selectedItem?.FileName,
								fileId: templateMetadata?.selectedItem?.Id,
								overwrite: true,
							};
							const response = await DocgenClient.copyFileFromSharefile(
								templateRID,
								payload
							);

							if (response !== null) {
								await fetchThumbnailUrl(documentTemplate?.rid);
							}
						} catch (_) {
							handleError();
						}
					}

					await getDocumentTemplate(templateRID);
					updateTemplateMetadata({ currentStep: 0 });
				} else {
					updateTemplateMetadata({ currentStep: 0 });
				}
			} catch (_) {
				handleError();
			}
		};

		onMount();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{documentTemplate && !isNaN(templateMetadata?.currentStep) ? (
				<Flex justify="center">
					{templateMetadata?.currentStep === 0 && <Start />}
					{templateMetadata?.currentStep === 1 && <Prepare />}
					{templateMetadata?.currentStep === 2 && (
						<Review onDrawerClose={onDrawerClose} app={props.app} />
					)}
				</Flex>
			) : (
				<Flex
					vertical
					justify="center"
					align="center"
					gap="small"
					style={{ height: '100vh' }}
				>
					<Spin size="large" fullscreen />
				</Flex>
			)}
			{templateMetadata?.drawerCloseHandlers && (
				<DeleteTemplateConfirmationModal
					documentTemplate={documentTemplate}
					resetStore={resetStore}
					templateMetadata={templateMetadata}
					updateTemplateMetadata={updateTemplateMetadata}
				/>
			)}
		</>
	);
};

const DeleteTemplateConfirmationModal = ({
	documentTemplate,
	resetStore,
	templateMetadata,
	updateTemplateMetadata,
}) => {
	const { close, cancel, callback } = templateMetadata?.drawerCloseHandlers ?? {};

	const [modal, contextHolder] = Modal.useModal();

	const { token } = theme.useToken();

	const handleConfirm = useCallback(async () => {
		if (documentTemplate?.rid) {
			// Delete template only if it is newly created i.e in Draft state. If the template is already created it will have Live status
			if (documentTemplate?.status === 'Draft') {
				// In Create flow delete the template and close the Drawer
				try {
					await DocgenClient.deleteTemplate(documentTemplate.rid);

					resetStore(); // Reset store which also closes the Confirmation Modal
					callback?.(); // execute callback function if any
					close?.(); // Close the Drawer
				} catch (err) {
					updateTemplateMetadata({ drawerCloseHandlers: null }); // Close the confirmation Modal
					cancel?.(); // Don't close the Drawer
					handleError(); // Show error notification
				}
			} else {
				// In Edit flow close the Drawer without deleting the template
				updateTemplateMetadata({ drawerCloseHandlers: null }); // Close the confirmation modal
				callback?.(); // execute callback function if any
				close?.(); // Close the Drawer
			}
		}
	}, [
		callback,
		cancel,
		close,
		documentTemplate?.rid,
		documentTemplate?.status,
		resetStore,
		updateTemplateMetadata,
	]);

	const handleCancel = useCallback(() => {
		updateTemplateMetadata({ drawerCloseHandlers: null }); // Close the confirmation modal
		cancel?.(); // Don't close the Drawer
	}, [cancel, updateTemplateMetadata]);

	useEffect(() => {
		const deleteTemplateModalConfig: ModalFuncProps = {
			title: t('discardTemplate.title'),
			content: (
				<Text
					style={{
						paddingBottom: token.paddingXXS,
					}}
				>
					{t('discardTemplate.content')}
				</Text>
			),
			okText: t('discardTemplate.confirmButtonText'),
			cancelText: t('discardTemplate.cancelButtonText'),
			centered: true,
		};
		const handleWarningForTemplateDeletion = async () => {
			const confirmed = await modal.confirm(deleteTemplateModalConfig);
			if (confirmed) {
				handleConfirm();
			} else {
				handleCancel();
			}
		};
		handleWarningForTemplateDeletion();
	}, [handleCancel, handleConfirm, modal, token.paddingXXS]);

	return contextHolder;
};

export const onDocumentTemplateClose = (callback?: any) => {
	// Intercept cancelInteraction when closing the Drawer
	const cancelInteractionPromise = new Promise<any>((resolve, reject) => {
		const drawerCloseHandlers = {
			close: resolve,
			cancel: reject,
			callback,
		};

		useAppStore.getState().updateTemplateMetadata({ drawerCloseHandlers });
	});
	return cancelInteractionPromise;
};
